'use client';

import { createContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { AnalyticsCoordinator } from '../../core/analytics-coordinator';
import { ReadinessController } from '../../core/readiness';
import type { AnalyticsClient } from '../../types/analytics-client';
import type { AnalyticsTransport } from '../../types/analytics-transport';
import { LOGGED_OUT_ANALYTICS_USER_CONTEXT } from '../../types/analytics';
import type { BaselineDigitalData, UserContextInput } from '../../types/digital-data';
import {
  clearAnalyticsCoordinatorExposure,
  exposeAnalyticsCoordinator,
} from '../browser/coordinator-debug-exposure';
import type { CoordinatorDebugOptions } from '../browser/coordinator-debug-exposure';
import {
  clearBrowserAnalyticsCoordinatorIfCurrent,
  setBrowserAnalyticsCoordinator,
} from '../browser/coordinator-singleton';
import { logBrowserAnalyticsDebug } from '../browser/debug-log';
import { LoggingTransport } from '../browser/logging-transport';

export type CoordinatorDebugConfig = Pick<CoordinatorDebugOptions, 'windowKey' | 'debugHint'>;

export type AnalyticsProviderProps = {
  transport: AnalyticsTransport;
  baseline?: BaselineDigitalData;
  isDebugEnabled?: boolean;
  /** Registers this coordinator for host-approved React consumers outside the provider tree. */
  shouldRegisterAsSingleton?: boolean;
  coordinatorDebugConfig?: CoordinatorDebugConfig;
  userContext: UserContextInput | null;
  readinessInstance?: ReadinessController;
  children: ReactNode;
};

export const AnalyticsCoordinatorContext = createContext<AnalyticsClient | null>(null);

export function AnalyticsProvider({
  transport,
  baseline,
  isDebugEnabled,
  shouldRegisterAsSingleton = false,
  coordinatorDebugConfig,
  userContext,
  readinessInstance,
  children,
}: AnalyticsProviderProps) {
  const debug = isDebugEnabled ?? false;
  const mountBaselineRef = useRef(baseline);

  const loggingTransport = useMemo(
    () => new LoggingTransport(transport, logBrowserAnalyticsDebug),
    [transport]
  );
  loggingTransport.setEnabled(debug);

  const { coordinator, readiness } = useMemo(() => {
    const analyticsReadiness =
      readinessInstance ??
      new ReadinessController({
        isTransportReady: () => loggingTransport.isCallable(),
      });

    if (loggingTransport.isCallable()) {
      analyticsReadiness.markReady();
    }

    const analyticsCoordinator = new AnalyticsCoordinator({
      transport: loggingTransport,
      readiness: analyticsReadiness,
      onEventLogEntry: (entry) => {
        if (loggingTransport.isDebugEnabled()) {
          logBrowserAnalyticsDebug('audit', { entry });
        }
      },
    });

    if (mountBaselineRef.current !== undefined) {
      analyticsCoordinator.setBaselineDigitalData(mountBaselineRef.current);
    }
    // Seeded here, not in an effect: descendant mount effects run first and would
    // otherwise enqueue beacons snapshotted as logged-out. Uses the live prop so a
    // coordinator rebuild after login still seeds the current user, not mount-time state.
    analyticsCoordinator.setUserContext(userContext ?? LOGGED_OUT_ANALYTICS_USER_CONTEXT);

    return { coordinator: analyticsCoordinator, readiness: analyticsReadiness };
    // userContext is intentionally omitted from deps: routine login/logout updates use the effect below.
    // When this memo does re-run (transport/readiness), the closure carries the current prop.
  }, [loggingTransport, readinessInstance]);

  useEffect(() => {
    if (baseline === undefined || baseline === mountBaselineRef.current) {
      return;
    }

    coordinator.setBaselineDigitalData(baseline);
  }, [baseline, coordinator]);

  useEffect(() => {
    coordinator.reconnect();
    return () => {
      coordinator.dispose();
    };
  }, [coordinator]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (shouldRegisterAsSingleton) {
      setBrowserAnalyticsCoordinator(coordinator);
    }
    if (coordinatorDebugConfig) {
      exposeAnalyticsCoordinator({
        coordinator,
        ...coordinatorDebugConfig,
        isDebugEnabled: debug,
      });
    }

    return () => {
      if (shouldRegisterAsSingleton) {
        clearBrowserAnalyticsCoordinatorIfCurrent(coordinator);
      }
      if (coordinatorDebugConfig) {
        clearAnalyticsCoordinatorExposure(coordinator, coordinatorDebugConfig.windowKey);
      }
    };
  }, [coordinator, coordinatorDebugConfig, debug, shouldRegisterAsSingleton]);

  useEffect(() => {
    /** Revalidates analytics transport readiness when a page is restored from the browser back/forward cache. */
    function handlePageShow(event: PageTransitionEvent) {
      if (!event.persisted) {
        return;
      }
      if (!readiness.revalidate()) {
        readiness.armNotReadyTimer();
      }
    }

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [readiness]);

  useEffect(() => {
    coordinator.setUserContext(userContext ?? LOGGED_OUT_ANALYTICS_USER_CONTEXT);
  }, [coordinator, userContext]);

  return (
    <AnalyticsCoordinatorContext.Provider value={coordinator}>
      {children}
    </AnalyticsCoordinatorContext.Provider>
  );
}
